// P2b-3 §3 / §4: per-row Meta name + Google ad-label resolvers used by the
// role grids' "Resolved Meta name" / "Resolved Google ad label" columns.
// Pure functions — same precedence rules as the row editor's preview
// (RowEditor.tsx), extracted so both grids share one implementation.
//
// Precedence (mirrors RowEditor):
//   - Template:   row.<*Template> if it's a non-null string → use it
//                 → otherwise effectiveMetaCampaignNameTemplate / Google
//                   walks campaign.<*Template> → brandProfile.<default*Template>
//                 → null  (caller renders an em-dash placeholder)
//   - Descriptor: same precedence — null / undefined on the row falls
//                 through to the campaign's descriptor.
//   - monthYear:  rollout.monthYearLabel via resolveMonthYear
//
// Substitution itself is delegated to renderTemplate (src/lib/templating.ts).
//
// Round-1 polish Fix 1: previously this resolver used a "snapshot wins,
// even when null" rule. That collapsed the prod case where a Campaign
// has no per-Campaign template (so the row's snapshot is also null on
// creation): we'd return null and never reach the brand default.
// RowEditor never had this bug because it walks the chain inline; the
// resolver now matches that semantic — null / undefined on the row
// snapshot is treated as "no row override, walk upward".

import type { Account } from '../types/account.js';
import type { AccountCampaign } from '../types/accountCampaign.js';
import type { Campaign } from '../types/campaign.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';
import type { FranchiseProfile } from '../types/franchiseProfile.js';
import type { Rollout } from '../types/rollout.js';
import {
  effectiveGoogleAdLabelTemplate,
  effectiveMetaCampaignNameTemplate,
} from './effectiveCampaignName.js';
import { resolveMonthYear } from './monthYear.js';
import { renderTemplate } from './templating.js';

export type ResolvedTemplate = {
  template: string | null;
  resolved: string | null;
};

type Args = {
  row: Pick<
    AccountCampaign,
    'campaignDescriptor' | 'metaCampaignNameTemplate' | 'googleAdLabelTemplate'
  >;
  campaign: Campaign | null;
  account: Pick<Account, 'name' | 'slug'>;
  locationProfile: FranchiseLocationProfile | null;
  brandProfile: FranchiseProfile | null;
  rollout: Pick<Rollout, 'monthYearLabel'>;
};

export function resolveRowMetaName(args: Args): ResolvedTemplate {
  const { row, campaign, account, locationProfile, brandProfile, rollout } = args;
  if (!campaign) return { template: null, resolved: null };
  const descriptor = pickRowOverride(row.campaignDescriptor, campaign.campaignDescriptor ?? null);
  const template = pickRowOverride(
    row.metaCampaignNameTemplate,
    effectiveMetaCampaignNameTemplate(campaign, brandProfile),
  );
  if (!template) return { template: null, resolved: null };
  const monthYear = resolveMonthYear(rollout.monthYearLabel);
  const { output } = renderTemplate(template, account, locationProfile, brandProfile, {
    campaignDescriptor: descriptor,
    monthYear,
  });
  return { template, resolved: output };
}

export function resolveRowGoogleAdLabel(args: Args): ResolvedTemplate {
  const { row, campaign, account, locationProfile, brandProfile, rollout } = args;
  if (!campaign) return { template: null, resolved: null };
  const descriptor = pickRowOverride(row.campaignDescriptor, campaign.campaignDescriptor ?? null);
  const template = pickRowOverride(
    row.googleAdLabelTemplate,
    effectiveGoogleAdLabelTemplate(campaign, brandProfile),
  );
  if (!template) return { template: null, resolved: null };
  const monthYear = resolveMonthYear(rollout.monthYearLabel);
  const { output } = renderTemplate(template, account, locationProfile, brandProfile, {
    campaignDescriptor: descriptor,
    monthYear,
  });
  return { template, resolved: output };
}

// Row-override precedence helper. Used for both the template AND the
// descriptor. Rule: a row override "wins" only when it carries a real
// non-empty string. null / undefined / empty-string all mean
// "no row override — walk upstream to the campaign + brand default".
//
// The previous implementation treated null as "AM explicitly cleared"
// and short-circuited; that bypassed the brand default in the common
// case where a Campaign has no per-Campaign template and the row
// snapshotted that null on creation. RowEditor's inline walk has
// always treated null + undefined the same way; this matches it.
function pickRowOverride<T extends string>(
  rowValue: T | null | undefined,
  fallback: T | null,
): T | null {
  if (typeof rowValue === 'string' && rowValue.length > 0) return rowValue;
  return fallback;
}
